import React from 'react';
import { IconType } from 'react-icons';
import {
  FaHeart, FaBrain, FaRunning, FaUsers, FaPray, FaLeaf, FaFire,
  FaAppleAlt, FaDumbbell, FaBed, FaWalking, FaSwimmer, FaBicycle,
  FaLaughBeam, FaMedal, FaHandsHelping, FaBookReader, FaPalette,
  FaMusic, FaGamepad, FaTheaterMasks, FaYinYang, FaSpa, FaMountain,
  FaSun, FaMoon, FaStar, FaGem, FaTree, FaWater, FaSeedling,
  FaSmile, FaLaugh, FaGrinStars, FaHeartbeat, FaCoffee, FaUtensils,
  FaTachometerAlt, FaChartLine, FaRegCalendarAlt,
  FaBullseye, FaInfoCircle, FaCheck, FaClipboardList, FaShieldAlt,
  FaQuestionCircle, FaExclamationTriangle,
  FaTimes, FaSearch, FaUser, FaUserTie, FaTrophy, FaCog, FaWrench,
  FaEye, FaEyeSlash, FaPencilAlt, FaTrashAlt, FaLock
} from 'react-icons/fa';
import { 
  GiMeditation, GiBrain, GiMuscleUp, GiStrongMan, GiWeightLiftingUp,
  GiLotusFlower, GiHeartBeats, GiNightSleep, GiHealthNormal,
  GiMeal, GiHotMeal, GiTeacher, GiBookCover, GiMusicalNotes,
  GiPaintBrush, GiChessKnight, GiCardPlay, GiFruitBowl, GiWaterBottle,
  GiMountains, GiForestCamp, GiFlowerPot, GiButterfly, GiPartyPopper
} from 'react-icons/gi';
import { 
  MdSelfImprovement, MdOutlineEmojiEmotions, MdPsychology, MdFavorite,
  MdNaturePeople, MdOutlineGroup, MdSpa, MdLocalFlorist
} from 'react-icons/md';

// Map of icon names to icon components
const iconMap: Record<string, IconType> = {
  // FontAwesome icons
  FaHeart, FaBrain, FaRunning, FaUsers, FaPray, FaLeaf, FaFire,
  FaAppleAlt, FaDumbbell, FaBed, FaWalking, FaSwimmer, FaBicycle,
  FaLaughBeam, FaMedal, FaHandsHelping, FaBookReader, FaPalette,
  FaMusic, FaGamepad, FaTheaterMasks, FaYinYang, FaSpa, FaMountain,
  FaSun, FaMoon, FaStar, FaGem, FaTree, FaWater, FaSeedling,
  FaSmile, FaLaugh, FaGrinStars, FaHeartbeat, FaCoffee, FaUtensils,
  FaTachometerAlt, FaChartLine, FaRegCalendarAlt,
  FaBullseye, FaInfoCircle, FaCheck, FaClipboardList, FaShieldAlt,
  FaQuestionCircle, FaExclamationTriangle,
  FaTimes, FaSearch, FaUser, FaUserTie, FaTrophy, FaCog, FaWrench,
  FaEye, FaEyeSlash, FaPencilAlt, FaTrashAlt, FaLock,

  // Game Icons
  GiMeditation, GiBrain, GiMuscleUp, GiStrongMan, GiWeightLiftingUp,
  GiLotusFlower, GiHeartBeats, GiNightSleep, GiHealthNormal,
  GiMeal, GiHotMeal, GiTeacher, GiBookCover, GiMusicalNotes,
  GiPaintBrush, GiChessKnight, GiCardPlay, GiFruitBowl, GiWaterBottle,
  GiMountains, GiForestCamp, GiFlowerPot, GiButterfly, GiPartyPopper,
  
  // Material Design icons
  MdSelfImprovement, MdOutlineEmojiEmotions, MdPsychology, MdFavorite,
  MdNaturePeople, MdOutlineGroup, MdSpa, MdLocalFlorist
};

interface IconProps {
  name: string;
  size?: number;
  className?: string;
  style?: React.CSSProperties;
  /** Purely decorative icon (redundant with adjacent text) — hidden from assistive tech. */
  decorative?: boolean;
}

/**
 * Universal Icon component that can render:
 * 1. React icon components (e.g., "FaHeart")
 * 2. Emojis (e.g., "💪")
 * 3. Fallback default if neither
 */
export const Icon: React.FC<IconProps> = ({ name, size = 20, className = '', style = {}, decorative = false }) => {
  const ariaHiddenProps = decorative ? { 'aria-hidden': true as const } : {};

  // Check if it's an emoji (simple check: if it's 1-2 chars and not alphanumeric)
  const isEmoji = name && name.length <= 2 && !/^[a-zA-Z0-9]+$/.test(name);

  if (isEmoji) {
    // Render emoji as text
    return (
      <span
        className={`icon-emoji ${className}`}
        style={{ fontSize: size, ...style }}
        {...ariaHiddenProps}
      >
        {name}
      </span>
    );
  }

  // Check if it's a registered icon component
  const IconComponent = iconMap[name] as React.ComponentType<{
    size?: number;
    color?: string;
    className?: string;
    style?: React.CSSProperties;
    'aria-hidden'?: boolean;
  }>;

  if (IconComponent) {
    // Extract color from style or use 'currentColor' to pick up parent color
    const iconColor = (style.color as string) || 'currentColor';

    return (
      <IconComponent
        size={size}
        color={iconColor}
        className={`icon-component ${className}`}
        style={style}
        {...ariaHiddenProps}
      />
    );
  }

  // Fallback: render default SVG icon when no name given, otherwise show the
  // unrecognized name as text
  if (!name) {
    const DefaultIcon = FaBullseye as React.ComponentType<{
      size?: number;
      color?: string;
      className?: string;
      style?: React.CSSProperties;
      'aria-hidden'?: boolean;
    }>;

    return (
      <DefaultIcon
        size={size}
        color={(style.color as string) || 'currentColor'}
        className={`icon-fallback ${className}`}
        style={style}
        {...ariaHiddenProps}
      />
    );
  }

  return (
    <span
      className={`icon-fallback ${className}`}
      style={{ fontSize: size, ...style }}
      {...ariaHiddenProps}
    >
      {name}
    </span>
  );
};

/**
 * Get a displayable preview of an icon (for admin UI)
 */
export const getIconPreview = (iconName: string): React.ReactNode => {
  return <Icon name={iconName} size={24} />;
};

/**
 * Check if a string is a valid icon reference
 */
export const isValidIcon = (iconName: string): boolean => {
  if (!iconName) return false;
  
  // Check if it's an emoji
  const isEmoji = iconName.length <= 2 && !/^[a-zA-Z0-9]+$/.test(iconName);
  if (isEmoji) return true;
  
  // Check if it's a registered icon
  return iconName in iconMap;
};

/**
 * Get all available icon names
 */
export const getAvailableIcons = (): string[] => {
  return Object.keys(iconMap);
};

